'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, CaptionResponse } from '@/lib/ai-models-api';

type CaptionMode = 'caption' | 'tags' | 'question';

export default function CaptionPage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mode, setMode] = useState<CaptionMode>('caption');
  const [question, setQuestion] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CaptionResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      let response: CaptionResponse;

      switch (mode) {
        case 'caption':
          response = await aiModelsApi.captionGenerate(image);
          break;
        case 'tags':
          response = await aiModelsApi.captionTags(image);
          break;
        case 'question':
          if (!question.trim()) {
            throw new Error('Please enter a question');
          }
          response = await aiModelsApi.captionQuestion(image, question.trim());
          break;
        default:
          throw new Error('Unknown mode');
      }

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">Caption - Image Understanding</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Mode</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as CaptionMode)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="caption">Generate Caption</option>
                    <option value="tags">Extract Tags</option>
                    <option value="question">Ask Question (VQA)</option>
                  </select>
                </div>

                {/* Question Input */}
                {mode === 'question' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Question</label>
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="What is in this image?"
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    />
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading || (mode === 'question' && !question.trim())}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : mode === 'caption' ? 'Generate Caption' : mode === 'tags' ? 'Extract Tags' : 'Ask Question'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image to analyze</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Analyzing image...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Caption */}
                    {result.caption && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Caption</p>
                        <p className="text-lg">{result.caption}</p>
                      </div>
                    )}

                    {/* Tags */}
                    {result.tags && result.tags.length > 0 && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Tags</p>
                        <div className="flex flex-wrap gap-2">
                          {result.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-[var(--accent-soft)] text-[var(--accent)] rounded-full text-sm font-medium"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Answer */}
                    {result.answer && (
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Answer</p>
                        <p className="text-lg">{result.answer}</p>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase">Inference Time</p>
                      <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={() => {
                        const text = result.caption || result.answer || result.tags?.join(', ') || '';
                        navigator.clipboard.writeText(text);
                      }}
                      className="w-full py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
